import axios from 'axios';

const resolveApiBaseUrl = () => {
  const raw = (process.env.REACT_APP_BACKEND_URL || '').trim();

  // Handle missing/invalid env values from builds (e.g. undefined/null strings).
  if (!raw || raw === 'undefined' || raw === 'null') {
    return '/api';
  }

  const normalized = raw.endsWith('/') ? raw.slice(0, -1) : raw;
  return `${normalized}/api`;
};

const api = axios.create({
  baseURL: resolveApiBaseUrl(),
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      // Redirect based on current path
      const path = window.location.pathname;
      if (path.includes('/seller/dashboard')) {
        window.location.href = '/seller/login';
      } else if (path.includes('/admin/dashboard')) {
        window.location.href = '/admin/login';
      } else if (path.includes('/customer')) {
        window.location.href = '/customer/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  sellerSignup: (data) => api.post('/auth/seller-signup', data),
  login: (data) => api.post('/auth/login', data),
  passwordReset: (email) => api.post('/auth/password-reset', { email }),
  changePassword: (data) => api.post('/auth/change-password', data),
};

// Customer Auth APIs
export const customerAuthAPI = {
  signup: (data) => api.post('/customer/signup', data),
  login: (data) => api.post('/customer/login', data),
  requestOTP: (email) => api.post('/customer/otp/request', { email }),
  verifyOTP: (email, otp) => api.post('/customer/otp/verify', { email, otp }),
  getProfile: () => api.get('/customer/profile'),
  getOrders: (params) => api.get('/customer/orders', { params }),
};

// Seller APIs
export const sellerAPI = {
  getProfile: () => api.get('/seller/profile'),
  updateProfile: (data) => api.put('/seller/profile', null, { params: data }),
  getStatistics: () => api.get('/seller/statistics'),
  getCustomers: (params) => api.get('/seller/customers', { params }),
};

// Category APIs
export const categoryAPI = {
  getAll: () => api.get('/categories'),
  create: (data) => api.post('/categories', data),
  update: (id, data) => api.put(`/categories/${id}`, data),
  delete: (id) => api.delete(`/categories/${id}`),
};

// Product APIs
export const productAPI = {
  getAll: () => api.get('/products'),
  create: (data) => api.post('/products', data),
  update: (id, data) => api.put(`/products/${id}`, data),
  delete: (id) => api.delete(`/products/${id}`),
};

// Order APIs
export const orderAPI = {
  getAll: (params) => api.get('/orders', { params }),
  action: (id, data) => api.put(`/orders/${id}/action`, data),
  updateDelivery: (id, data) => api.put(`/orders/${id}/delivery`, data),
};

// Shop APIs (Public)
export const shopAPI = {
  getShop: (slug, params = {}) => api.get(`/shop/${slug}`, { params }),
  getShopProducts: (slug, params = {}) => api.get(`/shop/${slug}/products`, { params }),
  getProduct: (slug, productId) => api.get(`/shop/${slug}/product/${productId}`),
  createOrder: (slug, data) => api.post(`/shop/${slug}/order`, data),
};

// Admin APIs
export const adminAPI = {
  getSellers: () => api.get('/admin/sellers'),
  updateSellerStatus: (id, status) => api.put(`/admin/sellers/${id}/status?status=${status}`),
  resetSellerPassword: (id, newPassword) => api.put(`/admin/sellers/${id}/reset-password`, { new_password: newPassword }),
  getSellerCustomers: (id) => api.get(`/admin/sellers/${id}/customers`),
  getStatistics: () => api.get('/admin/statistics'),
};

// Image upload
export const uploadImage = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/upload/image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export default api;